import { number, route, string, union } from 'react-router-typesafe-routes';
import { z } from 'zod';

export const handleSchema = z.object({
  showFooter: z.boolean().default(false),
});

export type RouteHandle = z.infer<typeof handleSchema>;

const collectionHashOptions = union(['all', 'media', 'text', 'files']);
const mediaHashOptions = union(['all', 'images', 'videos', 'files']);

const ROUTES = {
  ONBOARD: route({
    path: 'onboard',
    searchParams: {
      locale: string(),
      brand: string().defined(),
      token: string(),
      screen: string(),
      redirect: string(),
      layout: union(['mini', 'full']),
    },
  }),
  SEARCH: route({
    path: 'search',
    searchParams: {
      keyword: string(),
    },
    hash: union(['friends', 'groups', 'messages', 'all']),
    children: {
      BY_SESSION: route({
        path: ':session',
        params: {
          session: string().defined(),
        },
      }),
    },
  }),
  AUTH: route({
    path: 'auth',
    children: {
      SIGN_IN: route({ path: 'sign-in', searchParams: { phone: string(), redirect: string() } }),
      SIGN_UP: route({ path: 'sign-up', searchParams: { phone: string() } }),
      FORGOT_PASSWORD: route({ path: 'forgot-password' }),
      PRIVACY: route({ path: 'privacy' }),
      AGREEMENT: route({ path: 'agreement' }),
      SSO: route({
        path: 'sso',
        searchParams: {
          token: string(),
        },
      }),
    },
  }),
  USER_BY_ID: route({
    path: 'users/:id',
    params: { id: number().defined() },
    searchParams: {
      layout: union(['full', 'mini']),
      from: union(['onboard', 'comm']),
    },
  }),
  GROUP_BY_ID: route({
    path: 'groups/:id',
    params: { id: number().defined() },
    searchParams: { from: union(['onboard', 'comm']), remark: string() },
  }),
  CONTACT: route({
    path: 'contact',
    children: {
      GROUPS: route({
        path: 'groups',
        children: {
          CREATE: route({
            path: 'create',
            state: {
              step: number(),
            },
          }),
        },
      }),
      USERS: route({
        path: 'users',
        children: {
          APPLICATIONS: route({ path: 'applications' }),
          BY_ID: route({
            path: ':userId',
            params: { userId: number().defined() },
            searchParams: {
              source: union(['contact', 'search', 'avatar', 'qrcode']),
              groupId: number(),
            },
            state: {
              from: union(['contact', 'session']),
            },
            children: {
              REMARK: route({ path: 'remark' }),
              GROUPS: route({ path: 'groups' }),
              TAGS: route({ path: 'tags' }),
            },
          }),
        },
      }),
      REQUEST: route({
        path: 'request',
        children: {
          USER: route({
            path: 'user',
            searchParams: {
              id: number(),
              code: string(),
              key: string(),
              source: union(['contact', 'search', 'avatar', 'qrcode']),
              sessionId: number(),
              groupId: number(),
            },
            state: {
              from: union(['comm']),
            },
          }),
          GROUP: route({
            path: 'group',
            searchParams: {
              code: string(),
            },
          }),
          INVITE: route({
            path: 'invite',
            searchParams: {
              code: string(),
            },
          }),
          SCAN: route({
            path: 'scan',
            children: {
              SHARE: route({ path: 'share', searchParams: { code: string() } }),
            },
          }),
        },
      }),
      TAGS: route({
        path: 'tags',
        children: {
          BY_ID: route({
            path: ':tagId',
            params: { tagId: number().defined() },
            children: { ADD_MEMBER: route({ path: 'add-member' }) },
          }),
        },
      }),
    },
  }),
  MY: route({
    path: 'my',
    state: { from: union(['comm']) },
    children: {
      INFO: route({ path: 'info' }),
      DEVELOPER: route({
        path: 'developer',
        children: {
          MESSAGING: route({ path: 'messaging' }),
        },
      }),
      QR_CODE: route({
        path: 'qrcode',
        searchParams: { from: union(['onboard']) },
        children: {
          SHARE: route({ path: 'share', searchParams: { code: string() } }),
        },
      }),
      NICKNAME: route({ path: 'nickname' }),
      SIGNATURE: route({ path: 'signature' }),
      BLACKLIST: route({ path: 'blacklist' }),
      COLLECTIONS: route({
        path: 'collections',
        hash: collectionHashOptions,
        searchParams: { from: union(['onboard']) },
        state: { from: union(['comm']) },
        children: {
          BY_ID: route({
            path: ':collectionId',
            params: { collectionId: number().defined() },
            children: {
              FORWARD: route({ path: 'forward' }),
            },
          }),
        },
      }),
      SETTINGS: route({
        path: 'settings',
        children: {
          PRIVACY: route({ path: 'privacy', searchParams: { from: union(['onboard']) } }),
          MESSAGING: route({ path: 'messaging', searchParams: { from: union(['onboard']) } }),
          NOTIFICATIONS: route({ path: 'notifications' }),
          SECURITY: route({
            path: 'security',
            children: {
              PASSWORD: route({ path: 'password' }),
            },
          }),
          GENERAL: route({
            path: 'general',
            children: {
              ABOUT_US: route({ path: 'about-us' }),
              FEEDBACK: route({ path: 'feedback' }),
              DEBUG: route({ path: 'debug' }),
              LOGS: route({ path: 'logs' }),
            },
          }),
        },
      }),
    },
  }),
  MESSAGES: route({
    path: 'messages',
    searchParams: { from: union(['onboard']) },
    state: {
      from: union(['comm']),
    },
    children: {
      SESSIONS: route({
        path: 'sessions/:id',
        params: { id: number().defined() },
        searchParams: { search: number() },
        state: {
          from: union(['search', 'comm']),
          layout: union(['full', 'mini']),
        },
        children: {
          SETTINGS: route({
            path: 'settings',
            state: {
              from: union(['comm']),
            },
          }),
          FORWARD: route({
            path: 'forward',
            searchParams: {
              messageIds: number().array(),
            },
          }),
          SEARCH: route({ path: 'search', searchParams: { keyword: string() } }),
          CONTACT: route({ path: 'contact' }),
          COLLECTIONS: route({
            path: 'collections',
            hash: collectionHashOptions,
            children: {
              BY_ID: route({
                path: ':collectionId',
                params: { collectionId: number().default(0) },
              }),
            },
          }),
          MEDIA: route({
            path: 'media',
            hash: mediaHashOptions,
            children: {
              BY_ID: route({
                path: ':messageId',
                params: { messageId: number().defined() },
                state: { from: union(['message', 'index']) },
              }),
            },
          }),
          MESSAGE_BY_ID: route({
            path: ':messageId',
            params: { messageId: number().defined() },
          }),
          REPORT: route({
            path: 'report',
            searchParams: {
              reasonId: number(),
            },
          }),
        },
      }),
      GROUPS: route({
        path: 'groups/:id',
        params: { id: number().defined() },
        searchParams: { search: number() },
        state: {
          from: union(['search', 'comm']),
          layout: union(['full', 'mini']),
        },
        children: {
          SETTINGS: route({
            path: 'settings',
            state: {
              from: union(['comm']),
            },
            children: {
              NICKNAME: route({ path: 'nickname' }),
              ANNOUNCEMENT: route({ path: 'announcement' }),
              QR_CODE: route({
                path: 'qrcode',
                children: {
                  SHARE: route({ path: 'share', searchParams: { code: string() } }),
                },
              }),
              MEMBERS: route({
                path: 'members',
                children: {
                  INVITE: route({ path: 'invite' }),
                },
              }),
              ADMIN: route({
                path: 'admin',
                children: {
                  APPLICATIONS: route({ path: 'applications' }),
                  INFO: route({ path: 'info' }),
                  ADMINS: route({
                    path: 'admins',
                    children: {
                      ADD: route({ path: 'add' }),
                    },
                  }),
                },
              }),
            },
          }),
          MESSAGE_BY_ID: route({
            path: ':messageId',
            params: { messageId: number().defined() },
          }),
          FORWARD: route({
            path: 'forward',
            searchParams: {
              messageIds: number().array(),
            },
          }),
          SEARCH: route({ path: 'search', searchParams: { keyword: string() } }),
          CONTACT: route({ path: 'contact' }),
          COLLECTIONS: route({
            path: 'collections',
            hash: collectionHashOptions,
            children: {
              BY_ID: route({
                path: ':collectionId',
                params: { collectionId: number().default(0) },
              }),
            },
          }),
          MEDIA: route({
            path: 'media',
            hash: mediaHashOptions,
            children: {
              BY_ID: route({
                path: ':messageId',
                params: { messageId: number().defined() },
                state: { from: union(['message', 'index']) },
              }),
            },
          }),
          REPORT: route({
            path: 'report',
            searchParams: {
              reasonId: number(),
            },
          }),
        },
      }),
    },
  }),
};

export default ROUTES;

export type PromiseLoaderReturnType = Nullable<{ promises: Promise<void> }>;
